import React, { FC, ChangeEvent, useState, useEffect, useRef } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import { Filters } from './types';
import { ListingsSummary } from '../../graphql/generated';
import BrowsingFiltersDropdown from '../../Components/Menus/BrowsingFiltersDropdown';
import { FilterList, Search, Sort } from '@mui/icons-material';

interface ListingsCategoryFiltersProps {
  onFiltersSubmit: (filters: Filters) => void;
  listingsSummary: ListingsSummary;
}

const ListingsCategoryFilters: FC<ListingsCategoryFiltersProps> = ({
  onFiltersSubmit,
  listingsSummary,
}) => {
  const [brand, setBrand] = useState<string>('');
  const [size, setSize] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'asc' | 'desc' | ''>('newest');
  const isFirstSearchRender = useRef(true);

  // 🔹 Debounce search — skip the initial mount and any non-typed changes
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    const timeout = setTimeout(() => {
      handleSubmit(); // auto-trigger search after typing stops
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSortChange = (e: SelectChangeEvent<'newest' | 'asc' | 'desc' | ''>) => {
    const value = e.target.value as 'newest' | 'asc' | 'desc' | '';
    setSortOrder(value);
    handleSubmit({ sortOrder: value });
  };

  const handleSubmit = (updated?: Partial<Filters>) => {
    onFiltersSubmit({
      brand,
      size,
      search,
      sortOrder,
      ...updated,
    });
  };

  const handleSetBrand = (selectedBrand: string) => {
    setBrand(selectedBrand);
    handleSubmit({ brand: selectedBrand });
  };

  const handleSetSize = (selectedSize: string) => {
    setSize(selectedSize);
    handleSubmit({ size: selectedSize });
  };

  const handleClearFilters = () => {
    setBrand('');
    setSize('');
    setSearch('');
    setSortOrder('newest');
    onFiltersSubmit({ brand: '', size: '', search: '', sortOrder: 'newest' });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        p: 3,
        borderRadius: 2,
        mb: 3,
        width: '100%',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
      }}
    >
      {/* 🧭 Filters Label Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          px: 2,
          py: 0.5,
          backgroundColor: 'primary.main',
          color: '#fff',
          borderBottomRightRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FilterList fontSize="small" />
        <Typography variant="subtitle2" fontWeight={600}>
          Filters
        </Typography>
      </Box>

      {/* 🔹 Filters Row */}
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        width="100%"
        mt={3.5} // spacing to avoid overlapping the top badge
      >
        {/* Brand */}
        <Box flex="1 1 200px" minWidth="150px">
          <BrowsingFiltersDropdown
            title="Brand"
            options={listingsSummary.brands || []}
            setFilter={handleSetBrand}
            value={brand}
          />
        </Box>

        {/* Size */}
        <Box flex="1 1 200px" minWidth="150px">
          <BrowsingFiltersDropdown
            title="Size"
            options={listingsSummary.sizes || []}
            setFilter={handleSetSize}
            value={size}
          />
        </Box>

        {/* Search */}
        <Box flex="2 1 250px" minWidth="180px">
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Sort */}
        <Box flex="1 1 200px" minWidth="150px">
          <FormControl fullWidth size="small">
            <InputLabel id="sort-by-price-label">Sort</InputLabel>
            <Select
              labelId="sort-by-price-label"
              value={sortOrder}
              label="Sort"
              onChange={handleSortChange}
              startAdornment={
                <InputAdornment position="start">
                  <Sort color="action" />
                </InputAdornment>
              }
            >
              <MenuItem value="newest">Newest Arrivals</MenuItem>
              <MenuItem value="asc">Price: Low → High</MenuItem>
              <MenuItem value="desc">Price: High → Low</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Buttons */}
        <Box
          flex="1 1 auto"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
          minWidth="200px"
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClearFilters}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              fontWeight: 500,
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ListingsCategoryFilters;
